import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
    getPublicProfessionalTest,
    getPublicTestConsentDocument,
    submitPublicProfessionalResult,
} from "../services/publicProfessionalTestApi";
import { useProfTestTimer } from "../hooks/useProfTestTimer";
import { useTestIntegrity } from "../hooks/useTestIntegrity";
import ProfTestQuestionStep from "../components/professional-test/ProfTestQuestionStep";
import ProfTestTimerBar from "../components/professional-test/ProfTestTimerBar";
import {
    answersForSubmit,
    buildProfAnswerEntry,
    fillUnansweredAsTimedOut,
} from "../utils/profTestTake";

const PHASE_INTRO = "intro";
const PHASE_QUESTION = "question";
const PHASE_DONE = "done";
const ALREADY_COMPLETED_MESSAGE = "Вы уже проходили этот тест. Повторная отправка результатов недоступна.";

function questionCountLabel(count) {
    const value = Math.abs(Number(count) || 0);
    const mod100 = value % 100;
    const mod10 = value % 10;
    if (mod100 >= 11 && mod100 <= 14) return `${value} вопросов`;
    if (mod10 === 1) return `${value} вопрос`;
    if (mod10 >= 2 && mod10 <= 4) return `${value} вопроса`;
    return `${value} вопросов`;
}

export default function ProfessionalTakePage() {
    const { testId } = useParams();
    const qs = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
    const candidateIdFromLink = qs.get("candidate_id");
    const resultIdFromLink = qs.get("result_id");
    const candidateIdNum = Number(candidateIdFromLink);
    const resultIdNum = Number(resultIdFromLink);
    const linkedToCandidate =
        (Number.isFinite(candidateIdNum) && candidateIdNum > 0)
        || (Number.isFinite(resultIdNum) && resultIdNum > 0);
    const [test, setTest] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [phase, setPhase] = useState(PHASE_INTRO);
    const [fullName, setFullName] = useState("");
    const [position, setPosition] = useState("");
    const [takenAt, setTakenAt] = useState(() => new Date().toISOString().slice(0, 10));
    const [answers, setAnswers] = useState({});
    const [questionIndex, setQuestionIndex] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(null);
    const [alreadyCompleted, setAlreadyCompleted] = useState(false);
    const [consentAccepted, setConsentAccepted] = useState(false);
    const [consentPreview, setConsentPreview] = useState(null);
    const [consentLoading, setConsentLoading] = useState(false);
    const [, setTimedOut] = useState(false);
    const submittingRef = useRef(false);
    const answersRef = useRef(answers);
    const testStartedAtRef = useRef(null);
    const questionStartedAtRef = useRef(null);

    useEffect(() => {
        answersRef.current = answers;
    }, [answers]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await getPublicProfessionalTest(testId, {
                    candidate_id: Number.isFinite(candidateIdNum) && candidateIdNum > 0
                        ? candidateIdNum
                        : undefined,
                    result_id: Number.isFinite(resultIdNum) && resultIdNum > 0
                        ? resultIdNum
                        : undefined,
                });
                if (!cancelled) {
                    setTest(data);
                    setAlreadyCompleted(Boolean(data?.already_completed));
                }
            } catch (e) {
                if (!cancelled) setError(e.message || String(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [candidateIdNum, resultIdNum, testId]);

    const questions = useMemo(() => test?.questions || [], [test]);
    const currentQuestion = questions[questionIndex] || null;
    const inQuestions = phase === PHASE_QUESTION && Boolean(currentQuestion);
    const terminateRef = useRef(null);
    const integrity = useTestIntegrity({
        active: inQuestions,
        policy: test?.integrity,
        onLimitExceeded: (reason) => {
            if (typeof terminateRef.current === "function") terminateRef.current(reason);
        },
    });
    const integritySnapshot = integrity.snapshot;

    useEffect(() => {
        if (!inQuestions) return undefined;
        const handleVisibility = () => {
            if (document.hidden) {
                if (currentQuestion?.track_time && questionStartedAtRef.current) {
                    const key = String(currentQuestion.id);
                    const previous = answersRef.current[key] || buildProfAnswerEntry();
                    const nextAnswers = {
                        ...answersRef.current,
                        [key]: {
                            ...previous,
                            duration_seconds: Number(previous.duration_seconds || 0)
                                + Math.max(0, Math.floor((Date.now() - questionStartedAtRef.current) / 1000)),
                        },
                    };
                    answersRef.current = nextAnswers;
                    setAnswers(nextAnswers);
                }
                questionStartedAtRef.current = null;
            } else {
                questionStartedAtRef.current = Date.now();
            }
        };
        document.addEventListener("visibilitychange", handleVisibility);
        return () => document.removeEventListener("visibilitychange", handleVisibility);
    }, [currentQuestion, inQuestions]);

    const finalizeSubmit = useCallback(
        async ({ timedOut: isTimedOut = false, terminated = false, answersOverride = null } = {}) => {
            if (submittingRef.current) return;
            submittingRef.current = true;
            setSubmitting(true);
            setError(null);
            try {
                let sourceAnswers = answersOverride || answersRef.current;
                if (currentQuestion?.track_time && questionStartedAtRef.current) {
                    const key = String(currentQuestion.id);
                    const previousDuration = Number(sourceAnswers[key]?.duration_seconds || 0);
                    sourceAnswers = {
                        ...sourceAnswers,
                        [key]: {
                            ...(sourceAnswers[key] || buildProfAnswerEntry()),
                            duration_seconds: previousDuration + Math.max(0, Math.floor((Date.now() - questionStartedAtRef.current) / 1000)),
                        },
                    };
                }
                const prepared = isTimedOut || terminated
                    ? fillUnansweredAsTimedOut(sourceAnswers, questions)
                    : sourceAnswers;
                const payloadAnswers = answersForSubmit(prepared, questions);
                if (!Object.keys(payloadAnswers).length) {
                    questions.forEach((q) => {
                        payloadAnswers[String(q.id)] = buildProfAnswerEntry({
                            forcedIncorrect: true,
                        });
                    });
                }
                const result = await submitPublicProfessionalResult({
                    test_id: Number(testId),
                    candidate_id: Number.isFinite(candidateIdNum) && candidateIdNum > 0
                        ? candidateIdNum
                        : undefined,
                    result_id: Number.isFinite(resultIdNum) && resultIdNum > 0
                        ? resultIdNum
                        : undefined,
                    full_name: linkedToCandidate ? undefined : fullName.trim(),
                    position: linkedToCandidate ? undefined : position.trim(),
                    taken_at: linkedToCandidate ? undefined : takenAt,
                    answers: payloadAnswers,
                    timed_out: Boolean(isTimedOut),
                    personal_data_consent_accepted: Boolean(consentAccepted),
                    integrity: integritySnapshot({
                        durationSeconds: testStartedAtRef.current
                            ? Math.max(0, Math.floor((Date.now() - testStartedAtRef.current) / 1000))
                            : null,
                    }),
                });
                setDone(result);
                setPhase(PHASE_DONE);
            } catch (e) {
                if (e?.status === 409 || /уже проходили этот тест/i.test(e?.message || "")) {
                    setAlreadyCompleted(true);
                    setPhase(PHASE_INTRO);
                    setError(null);
                } else {
                    setError(e.message || String(e));
                }
                submittingRef.current = false;
            } finally {
                setSubmitting(false);
            }
        },
        [currentQuestion, fullName, position, takenAt, questions, testId, linkedToCandidate, candidateIdNum, resultIdNum, consentAccepted, integritySnapshot]
    );
    terminateRef.current = () => finalizeSubmit({ terminated: true });

    const totalSeconds =
        test?.duration_minutes != null && Number(test.duration_minutes) > 0
            ? Math.floor(Number(test.duration_minutes) * 60)
            : null;

    const { hasTimer, remainingSeconds, formatted } = useProfTestTimer(test?.duration_minutes, {
        enabled: inQuestions,
        onExpire: () => {
            setTimedOut(true);
            finalizeSubmit({ timedOut: true });
        },
    });

    const handleStart = () => {
        if (!linkedToCandidate && (!fullName.trim() || !position.trim() || !takenAt)) {
            setError("Заполните ФИО, должность и дату.");
            return;
        }
        if (test?.consent?.required && !consentAccepted) {
            setError("Откройте пользовательское соглашение и подтвердите согласие на обработку персональных данных.");
            return;
        }
        if (test?.external_url) {
            window.location.assign(test.external_url);
            return;
        }
        if (!questions.length) {
            setError("В тесте нет вопросов.");
            return;
        }
        setError(null);
        setAnswers({});
        setQuestionIndex(0);
        testStartedAtRef.current = Date.now();
        questionStartedAtRef.current = Date.now();
        submittingRef.current = false;
        setPhase(PHASE_QUESTION);
        integrity.requestFullscreen();
    };

    const openConsent = async () => {
        try {
            setConsentLoading(true);
            setError(null);
            const document = await getPublicTestConsentDocument(testId);
            setConsentPreview(document);
        } catch (e) {
            setError(e?.message || "Не удалось открыть пользовательское соглашение");
        } finally {
            setConsentLoading(false);
        }
    };

    const currentEntry = currentQuestion ? answers[String(currentQuestion.id)] : null;

    const selectOption = (optionIndex, optionText) => {
        if (!currentQuestion) return;
        setAnswers((prev) => {
            const key = String(currentQuestion.id);
            if (currentQuestion.answer_type === "multiple") {
                const current = prev[key]?.option_indexes || [];
                const indexes = current.includes(optionIndex)
                    ? current.filter((value) => value !== optionIndex)
                    : [...current, optionIndex];
                return {
                    ...prev,
                    [key]: buildProfAnswerEntry({
                        value: indexes.map((index) => currentQuestion.options[index]).join(", "),
                        optionIndexes: indexes,
                        customAnswer: prev[key]?.custom_answer || "",
                        durationSeconds: prev[key]?.duration_seconds,
                    }),
                };
            }
            return {
                ...prev,
                [key]: buildProfAnswerEntry({
                    value: optionText,
                    optionIndex,
                    customAnswer: "",
                    durationSeconds: prev[key]?.duration_seconds,
                }),
            };
        });
    };

    const changeText = (value) => {
        if (!currentQuestion) return;
        setAnswers((prev) => ({
            ...prev,
            [String(currentQuestion.id)]: buildProfAnswerEntry({
                value,
                optionIndex: null,
                durationSeconds: prev[String(currentQuestion.id)]?.duration_seconds,
            }),
        }));
    };

    const changeCustomAnswer = (value) => {
        if (!currentQuestion) return;
        setAnswers((prev) => {
            const key = String(currentQuestion.id);
            const previous = prev[key] || buildProfAnswerEntry();
            const keepChoices = currentQuestion.answer_type === "multiple";
            return {
                ...prev,
                [key]: buildProfAnswerEntry({
                    value: keepChoices
                        ? (previous.option_indexes || []).map((index) => currentQuestion.options[index]).join(", ")
                        : "",
                    optionIndex: keepChoices ? previous.option_index : null,
                    optionIndexes: keepChoices ? previous.option_indexes : [],
                    customAnswer: value,
                    durationSeconds: previous.duration_seconds,
                }),
            };
        });
    };

    const goNext = () => {
        let preparedAnswers = answersRef.current;
        if (currentQuestion?.track_time && questionStartedAtRef.current) {
            const key = String(currentQuestion.id);
            const previous = preparedAnswers[key] || buildProfAnswerEntry();
            preparedAnswers = {
                ...preparedAnswers,
                [key]: {
                    ...previous,
                    duration_seconds: Number(previous.duration_seconds || 0)
                        + Math.max(0, Math.floor((Date.now() - questionStartedAtRef.current) / 1000)),
                },
            };
            answersRef.current = preparedAnswers;
            setAnswers(preparedAnswers);
        }
        if (questionIndex >= questions.length - 1) {
            questionStartedAtRef.current = null;
            finalizeSubmit({ timedOut: false, answersOverride: preparedAnswers });
            return;
        }
        questionStartedAtRef.current = Date.now();
        setQuestionIndex((i) => i + 1);
    };

    const goPrevious = () => {
        if (questionIndex <= 0) return;
        let preparedAnswers = answersRef.current;
        if (currentQuestion?.track_time && questionStartedAtRef.current) {
            const key = String(currentQuestion.id);
            const previous = preparedAnswers[key] || buildProfAnswerEntry();
            preparedAnswers = {
                ...preparedAnswers,
                [key]: {
                    ...previous,
                    duration_seconds: Number(previous.duration_seconds || 0)
                        + Math.max(0, Math.floor((Date.now() - questionStartedAtRef.current) / 1000)),
                },
            };
            answersRef.current = preparedAnswers;
            setAnswers(preparedAnswers);
        }
        questionStartedAtRef.current = Date.now();
        setQuestionIndex((i) => i - 1);
    };

    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#f6f4fb] px-6 text-slate-500">
                <div className="flex items-center gap-3 rounded-2xl border border-violet-100 bg-white px-5 py-4 shadow-sm">
                    <span className="h-5 w-5 animate-spin rounded-full border-2 border-violet-200 border-t-violet-700" aria-hidden="true" />
                    Загрузка теста…
                </div>
            </div>
        );
    }

    if (error && !test) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#f6f4fb] p-6">
                <div className="w-full max-w-lg overflow-hidden rounded-3xl border border-rose-100 bg-white text-center shadow-xl shadow-slate-900/5">
                    <div className="h-2 bg-rose-500" />
                    <div className="p-8">
                        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 text-xl text-rose-600">!</div>
                        <h1 className="mb-2 text-xl font-bold text-slate-900">Не удалось открыть тест</h1>
                        <p className="text-sm leading-relaxed text-slate-600">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (alreadyCompleted) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#f6f4fb] p-6" data-testid="prof-already-completed">
                <div className="w-full max-w-lg overflow-hidden rounded-3xl border border-amber-200 bg-white text-center shadow-[0_24px_80px_rgba(76,29,149,0.10)]">
                    <div className="h-2 bg-gradient-to-r from-amber-400 via-orange-400 to-violet-600" />
                    <div className="p-8 md:p-10">
                        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-amber-50 text-3xl text-amber-700 ring-8 ring-amber-50/60" aria-hidden="true">✓</div>
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-700">Повторное прохождение недоступно</p>
                        <h1 className="mt-2 text-3xl font-bold text-slate-900">Тест уже пройден</h1>
                        {test?.name ? <p className="mt-2 font-medium text-slate-700">{test.name}</p> : null}
                        <p className="mt-4 text-sm leading-6 text-slate-600">{ALREADY_COMPLETED_MESSAGE}</p>
                        <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 text-sm text-slate-600">
                            Ваши предыдущие ответы уже сохранены и доступны ответственному сотруднику.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (phase === PHASE_DONE || done) {
        return (
            <div
                className="flex min-h-screen items-center justify-center bg-[#f6f4fb] p-6"
                data-testid="prof-take-done"
            >
                <div className="w-full max-w-lg overflow-hidden rounded-3xl border border-violet-100 bg-white text-center shadow-[0_24px_80px_rgba(76,29,149,0.12)]">
                    <div className="h-2 bg-gradient-to-r from-violet-700 via-indigo-600 to-amber-400" />
                    <div className="p-8 md:p-10">
                        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-8 ring-emerald-50/60">
                            <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-violet-700">Тест завершён</p>
                        <h1 className="mt-2 text-3xl font-bold text-slate-900">Спасибо!</h1>
                        <p className="mt-2 text-sm text-slate-600">Ответы сохранены.</p>
                        {done?.max_score != null && done.max_score > 0 && (
                            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4" data-testid="prof-score">
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Результат</p>
                                <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
                                    {done.score ?? 0} <span className="text-base font-medium text-slate-400">/ {done.max_score}</span>
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (phase === PHASE_INTRO) {
        return (
            <div className="relative min-h-screen overflow-hidden bg-[#f6f4fb]" data-testid="prof-take-page">
                <div className="pointer-events-none absolute -left-24 top-20 h-72 w-72 rounded-full bg-violet-200/30 blur-3xl" />
                <div className="pointer-events-none absolute -right-32 bottom-10 h-80 w-80 rounded-full bg-amber-100/50 blur-3xl" />
                <div className="relative mx-auto max-w-3xl px-4 py-8 md:py-12">
                    <section className="overflow-hidden rounded-[28px] border border-violet-100 bg-white shadow-[0_24px_80px_rgba(76,29,149,0.10)]">
                        <div className="h-2 bg-gradient-to-r from-violet-700 via-indigo-600 to-amber-400" />
                        <header className="border-b border-slate-100 px-6 py-7 md:px-9 md:py-9">
                            <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-violet-50 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-violet-700 ring-1 ring-violet-100">
                                <span className="h-1.5 w-1.5 rounded-full bg-violet-600" />
                                Профессиональный тест
                            </div>
                            <h1 className="text-3xl font-bold leading-tight tracking-tight text-slate-900 md:text-4xl">
                                {test.name}
                            </h1>
                            {test.description && (
                                <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-slate-600">{test.description}</p>
                            )}
                            <div className="mt-6 flex flex-wrap gap-2">
                                <span className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600">
                                    {questionCountLabel(questions.length)}
                                </span>
                                {test.duration_minutes ? (
                                    <span className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600">
                                        ≈ {test.duration_minutes} мин.
                                    </span>
                                ) : null}
                                <span className="rounded-full bg-rose-50 px-3 py-1.5 text-xs font-medium text-rose-700">
                                    * Обязательные вопросы
                                </span>
                            </div>
                        </header>

                        <div className="space-y-5 bg-[#faf9fd] p-4 md:p-7">
                            {test.instruction && (
                                <div className="rounded-2xl border border-violet-100 bg-violet-50/70 p-4 text-sm leading-relaxed text-violet-950 md:p-5">
                                    <p className="mb-1 text-xs font-bold uppercase tracking-wide text-violet-700">Инструкция</p>
                                    {test.instruction}
                                </div>
                            )}

                            <div className="rounded-2xl border border-amber-200/70 bg-amber-50/70 p-4 text-sm text-amber-950 md:p-5">
                                <div className="flex items-start gap-3">
                                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700" aria-hidden="true">i</span>
                                    <div>
                                        <p className="font-semibold">Перед началом</p>
                                        <ul className="mt-2 space-y-1.5 text-xs leading-relaxed text-amber-900/80 md:text-sm">
                                            <li>• Вопросы открываются по одному, возврат назад недоступен.</li>
                                            <li>• Выберите или введите ответ и нажмите «Далее».</li>
                                            {test.duration_minutes ? (
                                                <li>• После окончания времени ответы отправятся автоматически.</li>
                                            ) : null}
                                            <li>• Во время теста фиксируются переходы на другие вкладки и программы, время вне теста и копирование текста.</li>
                                            {integrity.policy.strict ? (
                                                <li data-testid="prof-strict-rules">• Строгий режим: копирование и вставка отключены. Если покинуть страницу больше {integrity.policy.max_tab_switches} раз или пробыть вне теста {Math.round(integrity.policy.max_away_seconds / 60)} мин и дольше, тест завершится автоматически.</li>
                                            ) : null}
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            {!linkedToCandidate && (
                                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
                                    <h2 className="text-base font-semibold text-slate-900">Данные участника</h2>
                                    <p className="mt-1 text-xs text-slate-500">Заполните поля перед началом теста.</p>
                                    <div className="mt-5 grid gap-4 md:grid-cols-2">
                                        <label className="block text-sm md:col-span-2">
                                            <span className="font-medium text-slate-700">ФИО <span className="text-rose-500">*</span></span>
                                            <input
                                                className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-violet-500 focus:bg-white focus:ring-4 focus:ring-violet-100"
                                                value={fullName}
                                                onChange={(e) => setFullName(e.target.value)}
                                                placeholder="Иванов Иван Иванович"
                                                autoComplete="name"
                                                data-testid="prof-full-name"
                                            />
                                        </label>
                                        <label className="block text-sm">
                                            <span className="font-medium text-slate-700">Должность <span className="text-rose-500">*</span></span>
                                            <input
                                                className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-slate-900 outline-none transition focus:border-violet-500 focus:bg-white focus:ring-4 focus:ring-violet-100"
                                                value={position}
                                                onChange={(e) => setPosition(e.target.value)}
                                                placeholder="Например, главный юрист"
                                                data-testid="prof-position"
                                            />
                                        </label>
                                        <label className="block text-sm">
                                            <span className="font-medium text-slate-700">Дата <span className="text-rose-500">*</span></span>
                                            <input
                                                type="date"
                                                className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-slate-900 outline-none transition focus:border-violet-500 focus:bg-white focus:ring-4 focus:ring-violet-100"
                                                value={takenAt}
                                                onChange={(e) => setTakenAt(e.target.value)}
                                                data-testid="prof-taken-at"
                                            />
                                        </label>
                                    </div>
                                </div>
                            )}

                            {test.consent?.required && (
                                <div className="rounded-2xl border border-violet-200 bg-white p-5 shadow-sm md:p-6">
                                    <h2 className="text-base font-semibold text-slate-900">Обработка персональных данных</h2>
                                    <p className="mt-1 text-sm leading-6 text-slate-600">
                                        Перед началом ознакомьтесь с документом и подтвердите согласие.
                                    </p>
                                    <button type="button" onClick={openConsent} disabled={consentLoading}
                                        className="mt-3 text-left text-sm font-semibold text-violet-700 underline decoration-violet-300 underline-offset-4 hover:text-violet-900 disabled:opacity-50"
                                        data-testid="prof-consent-open">
                                        {consentLoading ? "Открываем документ…" : `Согласие на обработку персональных данных${test.consent.filename ? ` — ${test.consent.filename}` : ""}`}
                                    </button>
                                    <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-xl bg-violet-50 p-4 text-sm text-slate-700">
                                        <input type="checkbox" checked={consentAccepted}
                                            onChange={(event) => setConsentAccepted(event.target.checked)}
                                            className="mt-0.5 h-5 w-5 shrink-0 accent-violet-700"
                                            data-testid="prof-consent-checkbox" />
                                        <span>Я ознакомился(ась) с документом и даю согласие на обработку персональных данных.</span>
                                    </label>
                                </div>
                            )}

                            {error && (
                                <p className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" role="alert">
                                    {error}
                                </p>
                            )}

                            <div className="flex flex-col items-center gap-3 pt-1">
                                <button
                                    type="button"
                                    onClick={handleStart}
                                    data-testid="prof-start"
                                    className="w-full rounded-xl bg-violet-700 px-6 py-3.5 font-semibold text-white shadow-md shadow-violet-900/10 transition hover:bg-violet-800 focus:outline-none focus:ring-4 focus:ring-violet-200 md:w-auto md:min-w-[220px]"
                                >
                                    Начать тест
                                </button>
                                <p className="text-center text-[11px] text-slate-400">Нажимая кнопку, вы подтверждаете готовность начать тестирование.</p>
                            </div>
                        </div>
                    </section>
                    {consentPreview?.url && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-3 md:p-6"
                            role="dialog" aria-modal="true" aria-label="Пользовательское соглашение">
                            <div className="flex h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                                <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-4 py-3 md:px-6">
                                    <div className="min-w-0">
                                        <h2 className="font-semibold text-slate-900">Согласие на обработку персональных данных</h2>
                                        <p className="truncate text-xs text-slate-500">{consentPreview.filename}</p>
                                    </div>
                                    <button type="button" onClick={() => setConsentPreview(null)}
                                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                                        data-testid="prof-consent-close">Закрыть</button>
                                </div>
                                <iframe title="Пользовательское соглашение" src={consentPreview.url}
                                    className="min-h-0 flex-1 bg-slate-100" />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#f6f4fb]" data-testid="prof-take-page">
            {hasTimer && (
                <ProfTestTimerBar
                    formatted={formatted}
                    remainingSeconds={remainingSeconds}
                    totalSeconds={totalSeconds}
                />
            )}

            {integrity.policy.strict && integrity.warning ? (
                <div className="mx-auto mt-4 max-w-3xl rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800" role="alert" data-testid="prof-integrity-warning">
                    Вы покидали страницу теста: {integrity.warning.leaves} из {integrity.warning.maxLeaves} допустимых, вне теста {Math.round(integrity.warning.awaySeconds)} сек из {integrity.warning.maxAwaySeconds}. При превышении тест завершится.
                </div>
            ) : null}

            {submitting ? (
                <div className="flex min-h-[60vh] items-center justify-center text-slate-500">
                    <div className="flex items-center gap-3 rounded-2xl border border-violet-100 bg-white px-5 py-4 shadow-sm">
                        <span className="h-5 w-5 animate-spin rounded-full border-2 border-violet-200 border-t-violet-700" aria-hidden="true" />
                        Отправка результатов…
                    </div>
                </div>
            ) : (
                <ProfTestQuestionStep
                    question={currentQuestion}
                    index={questionIndex}
                    total={questions.length}
                    selectedOptionIndex={currentEntry?.option_index ?? null}
                    selectedOptionIndexes={currentEntry?.option_indexes || []}
                    customAnswer={currentEntry?.custom_answer || ""}
                    textValue={currentEntry?.value || ""}
                    onSelectOption={selectOption}
                    onTextChange={changeText}
                    onCustomAnswerChange={changeCustomAnswer}
                    onPrevious={goPrevious}
                    onNext={goNext}
                    isLast={questionIndex >= questions.length - 1}
                />
            )}

            {error && (
                <p className="text-center text-sm text-red-600 pb-6" role="alert">
                    {error}
                </p>
            )}
        </div>
    );
}
